<script lang="ts">
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import type { CartType } from "@shared/types/Cart";
  import type { CartProduct } from "@shared/types/ProductLine";
  import { createEventDispatcher } from "svelte";

  export let cartProduct: CartProduct = null;
  export let show: boolean = false;

  const permanentDealNoteUrl = "api/retailer/deal_notes";
  const dealNoteUpdateUrl = `api/retailer/cart_products/${cartProduct.id}`;
  const dispatch = createEventDispatcher();

  let dealNote: string = "";
  let permanent: boolean = false;
  let submitting: boolean = false;

  const close = () => {
    show = false;
  };

  const dispatchUpdate = () => {
    dispatch("update", {
      cartProduct,
    });
  };

  const deleteNote = async () => {
    if (!!cartProduct.permanent_deal_notes) {
      await deletePermanentDealNote(cartProduct.permanent_deal_notes);
    } else if (!!cartProduct.deal_notes) {
      await deleteDealNote(cartProduct);
    }

    Flash.now({ type: "success", message: "Deal note succesfully deleted." });
    close();
    dealNote = "";
    dispatchUpdate();
  };

  const deletePermanentDealNote = async (permanentDealNote): Promise<void> => {
    await HttpPromise({
      url: permanentDealNoteUrl + "/" + permanentDealNote.id,
      method: "DELETE",
    })
      .catch(err => {
        Flash.now({
          type: "error",
          message:
            "There was an error updating this deal note. If it persists contact support for help.",
        });
      })
      .then(res => {
        cartProduct.permanent_deal_notes = null;
        permanent = false;
      });
    dispatchUpdate();
  };

  const deleteDealNote = async (cartProduct: CartProduct): Promise<void> => {
    await HttpPromise({
      url: dealNoteUpdateUrl,
      method: "PUT",
      data: { cart_product: { deal_notes: "" } },
    })
      .catch(err => {
        Flash.now({
          type: "error",
          message:
            "There was an error updating this deal note. If it persists contact support for help.",
        });
      })
      .then((res: CartType) => {
        cartProduct.deal_notes = res.orders
          .find(order => order.id == cartProduct.order_id)
          .products.find(product => product.id == cartProduct.id).deal_notes;
      });
    dispatchUpdate();
  };

  const save = async (): Promise<void> => {
    submitting = true;

    if (permanent) {
      if (!!cartProduct.deal_notes) await deleteDealNote(cartProduct);

      await HttpPromise({
        url: permanentDealNoteUrl,
        method: "POST",
        data: {
          cart_product_id: cartProduct.id,
          inventory_id: cartProduct.inventory.id,
          notes: dealNote,
          product_id: cartProduct.product_id,
          order_id: cartProduct.order_id,
        },
      })
        .catch(err => {
          err.errors.messages.forEach(message => {
            Flash.now({ type: "error", message });
          });
          return close();
        })
        .then(res => {
          cartProduct.permanent_deal_notes = {
            id: res.id,
            notes: res.notes,
          };
          Flash.now({ type: "success", message: "Permanent deal note saved successfully!" });
        });
    } else {
      if (!!cartProduct.permanent_deal_notes) {
        await deletePermanentDealNote(cartProduct.permanent_deal_notes);
      }

      await HttpPromise({
        url: dealNoteUpdateUrl,
        method: "PUT",
        data: {
          cart_product: {
            deal_notes: dealNote,
          },
        },
      })
        .catch(err => {
          Flash.now({
            type: "error",
            message:
              "There was an error updating this deal note. If it persists contact support for help.",
          });
          return close();
        })
        .then((res: CartType) => {
          cartProduct.deal_notes = res.orders
            .find(order => order.id == cartProduct.order_id)
            .products.find(product => product.id == cartProduct.id).deal_notes;
          Flash.now({ type: "success", message: "Deal note saved successfully!" });
        });
    }

    close();
    submitting = false;
    dispatchUpdate();
  };

  if (!!cartProduct.permanent_deal_notes) {
    dealNote = cartProduct.permanent_deal_notes.notes;
    permanent = true;
  } else if (!!cartProduct.deal_notes) {
    dealNote = cartProduct.deal_notes;
  } else {
    dealNote = "";
  }

  $: cartProduct;

</script>

<Modal bind:show fizzModal={true} size={"medium"}>
  <div class="layout-header-footer fizz-text-left">
    <header class="fizz-modal-header">
      <h4 class="fizz-heading-4">Deal note for {cartProduct.product_line.name}</h4>
      <span class="fizz-modal-toggle fizz-modal-close cursor-pointer" on:click={close}>
        <Icon height={"1rem"} type={"X"} width={"1rem"} />
      </span>
    </header>
    <section class="fizz-modal-main fizz-text-medium fizz-flow-16" data-grid-area="main">
      <p>Include a deal note to send to your sales rep</p>
      <textarea
        class="fizz-full-width fizz-textarea"
        name="deal-note-textarea"
        data-testid="deal-note-textarea"
        style="resize: none;"
        bind:value={dealNote}
      />
      <div class="fizz-flex-row-8">
        <input
          type="checkbox"
          id="permanent-deal-note-checkbox"
          data-testid="permanent-deal-note-checkbox"
          class="fizz-checkbox"
          bind:checked={permanent}
        />
        <label for="permanent-deal-note-checkbox" class="fizz-text">
          Use this deal note for every order
        </label>
      </div>
    </section>
    <footer class="fizz-modal-footer fizz-flex-row">
      {#if !!cartProduct.deal_notes || !!cartProduct.permanent_deal_notes}
        <button class="fizz-button-danger fizz-button" on:click={deleteNote}>
          Delete deal note
        </button>
      {/if}
      <div class="fizz-flex-row-8 fizz-full-width fizz-justify-end">
        <button class="fizz-button fizz-button" on:click={close}>Cancel</button>
        <button
          class="fizz-button-secondary fizz-button"
          on:click={save}
          disabled={!dealNote || submitting}
        >
          Save
        </button>
      </div>
    </footer>
  </div>
</Modal>
